import { Button, cn, Input } from '@kalos/ui';
import { ChevronRight } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';

import { DialogOrSheet } from '../../../components/DialogOrSheet';
import { MoneyInput } from '../../../components/MoneyInput';
import { cleanMoney } from '../../../tools/cleanMoney';

const money = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export type DiscountChoice = { kind: 'percent' | 'amount'; value: number };

type AdjustmentRow = {
  /** What it is now in words, e.g. "10%" or "$50.00 off"; nothing when there is none. */
  detail?: string;
  amount: number;
  /** Opens the sheet that changes it; without it the row is read-only. */
  onEdit?: () => void;
};

const Row = ({
  label,
  value,
  strong,
  muted,
}: {
  label: ReactNode;
  value: ReactNode;
  strong?: boolean;
  muted?: boolean;
}) => (
  <div
    className={cn(
      'flex min-h-9 items-center justify-between gap-3 text-sm',
      strong && 'text-base font-semibold',
      muted && 'text-muted-foreground',
    )}
  >
    <span>{label}</span>
    {/* An arrow-wide space even with no arrow, so every amount lines up in one column with
        the line items' and the tappable rows'. */}
    <span className="flex items-center gap-3 tabular-nums">
      {value}
      <span className="w-4 shrink-0" aria-hidden />
    </span>
  </div>
);

const AdjustmentLine = ({
  label,
  row,
  sign,
}: {
  label: string;
  row: AdjustmentRow;
  sign: '−' | '+';
}) => {
  const hasValue = row.amount > 0;
  const content = (
    <>
      <span className="flex min-w-0 items-baseline gap-2">
        <span>{label}</span>
        {row.detail && <span className="text-muted-foreground text-xs">{row.detail}</span>}
      </span>
      <span className="flex items-center gap-3 tabular-nums">
        {hasValue ? (
          `${sign}${money(row.amount)}`
        ) : (
          <span className="text-muted-foreground">{row.onEdit ? 'Add' : 'None'}</span>
        )}
        {row.onEdit ? (
          <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <span className="w-4 shrink-0" aria-hidden />
        )}
      </span>
    </>
  );
  return row.onEdit ? (
    <button
      type="button"
      onClick={row.onEdit}
      aria-label={hasValue ? `Change ${label.toLowerCase()}` : `Add ${label.toLowerCase()}`}
      className="active:bg-muted -mx-4 flex min-h-11 items-center justify-between gap-3 px-4 text-left text-sm"
    >
      {content}
    </button>
  ) : (
    <div className="flex min-h-9 items-center justify-between gap-3 text-sm">{content}</div>
  );
};

/**
 * The money at the foot of a proposal or an invoice on a phone or tablet, the same on both:
 * Subtotal, then Discount and Sales tax (each a row you tap to change), then the total. When
 * something has been paid, Paid and Balance due follow; otherwise the total is the last line,
 * so there's never a total and a balance showing the same number.
 */
export const FieldTotals = ({
  subtotal,
  discount,
  tax,
  total,
  totalLabel = 'Total',
  paid = 0,
  balanceDue,
  before,
}: {
  subtotal: number;
  discount: AdjustmentRow;
  /** Left out where the total carries no tax (a signed proposal's approved total). */
  tax?: AdjustmentRow;
  total: number;
  totalLabel?: string;
  paid?: number;
  balanceDue?: number;
  /** Lines that come before the subtotal (a signed proposal's declined services). */
  before?: ReactNode;
}) => (
  // No rule on top: the list above already ends in one.
  <div className="mt-1 flex flex-col pt-1">
    {before}
    <Row label="Subtotal" value={money(subtotal)} />
    <AdjustmentLine label="Discount" row={discount} sign="−" />
    {tax && <AdjustmentLine label="Sales tax" row={tax} sign="+" />}
    <div className="mt-1 border-t pt-1">
      <Row label={totalLabel} value={money(total)} strong={paid <= 0} />
      {paid > 0 && (
        <>
          <Row label="Paid" value={`−${money(paid)}`} muted />
          <Row label="Balance due" value={money(balanceDue ?? total - paid)} strong />
        </>
      )}
    </div>
  </div>
);

export { Row as FieldTotalsRow };

/**
 * Change the discount: percent or dollars, one number, Save. Remove sits apart when there is
 * one. Mount it while it's open so it starts from the saved discount.
 */
export const DiscountSheet = ({
  open,
  onOpenChange,
  initial,
  onSave,
  onRemove,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: DiscountChoice;
  /** Resolves true once saved; the sheet closes then. */
  onSave: (next: DiscountChoice) => Promise<boolean>;
  onRemove?: () => Promise<boolean>;
}) => {
  const [kind, setKind] = useState(initial.kind);
  const [value, setValue] = useState(initial.value > 0 ? String(initial.value) : '');
  const [isSaving, setIsSaving] = useState(false);
  const inputId = useId();

  const parsed = parseFloat(value);
  const error =
    value.trim() === ''
      ? null
      : isNaN(parsed) || parsed < 0
        ? 'Enter 0 or more.'
        : kind === 'percent' && parsed > 100
          ? 'A percent discount can be at most 100.'
          : null;
  const canSave = value.trim() !== '' && !error && !isSaving;

  const run = async (action: () => Promise<boolean>) => {
    setIsSaving(true);
    try {
      if (await action()) onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DialogOrSheet
      open={open}
      onOpenChange={(next) => !isSaving && onOpenChange(next)}
      title="Discount"
      isBusy={isSaving}
    >
      <div className="flex flex-col gap-4">
        <div role="radiogroup" aria-label="Discount type" className="bg-muted flex rounded-md p-1">
          {(
            [
              { kind: 'percent', label: 'Percent' },
              { kind: 'amount', label: 'Dollars' },
            ] as const
          ).map((option) => (
            <button
              key={option.kind}
              type="button"
              role="radio"
              aria-checked={kind === option.kind}
              onClick={() => setKind(option.kind)}
              disabled={isSaving}
              className={cn(
                'min-h-10 flex-1 rounded text-sm font-medium',
                kind === option.kind ? 'bg-card shadow-sm' : 'text-muted-foreground',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={inputId} className="text-sm font-medium">
            {kind === 'percent' ? 'Percent off' : 'Dollars off'}
          </label>
          {kind === 'amount' ? (
            <MoneyInput
              id={inputId}
              value={value}
              onValueChange={setValue}
              disabled={isSaving}
              autoFocus
            />
          ) : (
            <div className="relative">
              <Input
                id={inputId}
                type="text"
                inputMode="decimal"
                value={value}
                onChange={(event) => setValue(cleanMoney(event.target.value))}
                onFocus={(event) => event.target.select()}
                disabled={isSaving}
                className="pr-8 text-right tabular-nums"
                autoFocus
              />
              <span className="text-muted-foreground pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm">
                %
              </span>
            </div>
          )}
          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
        <Button
          disabled={!canSave}
          isLoading={isSaving}
          onClick={() => void run(() => onSave({ kind, value: parsed }))}
        >
          {isSaving ? 'Saving...' : 'Save discount'}
        </Button>
        {onRemove && (
          <Button
            variant="ghost"
            className="text-destructive"
            disabled={isSaving}
            onClick={() => void run(onRemove)}
          >
            Remove discount
          </Button>
        )}
      </div>
    </DialogOrSheet>
  );
};

const TAX_PRESETS = ['6', '7', '7.5'];

/** Change the sales tax: the usual rates one tap away, or type one. */
export const TaxSheet = ({
  open,
  onOpenChange,
  initialRate,
  onSave,
  onRemove,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialRate: number;
  onSave: (rate: number) => Promise<boolean>;
  onRemove?: () => Promise<boolean>;
}) => {
  const [value, setValue] = useState(initialRate > 0 ? String(initialRate) : '');
  const [isSaving, setIsSaving] = useState(false);
  const inputId = useId();

  const parsed = parseFloat(value);
  const error =
    value.trim() !== '' && (isNaN(parsed) || parsed < 0 || parsed > 100)
      ? 'Enter a rate from 0 to 100.'
      : null;
  const canSave = value.trim() !== '' && !error && !isSaving;

  const run = async (action: () => Promise<boolean>) => {
    setIsSaving(true);
    try {
      if (await action()) onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DialogOrSheet
      open={open}
      onOpenChange={(next) => !isSaving && onOpenChange(next)}
      title="Sales tax"
      isBusy={isSaving}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          {TAX_PRESETS.map((preset) => (
            <Button
              key={preset}
              type="button"
              variant={value === preset ? 'default' : 'outline'}
              className="min-w-16 rounded-full"
              aria-pressed={value === preset}
              disabled={isSaving}
              onClick={() => setValue(preset)}
            >
              {preset}%
            </Button>
          ))}
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={inputId} className="text-sm font-medium">
            Rate
          </label>
          <div className="relative">
            <Input
              id={inputId}
              type="text"
              inputMode="decimal"
              value={value}
              onChange={(event) => setValue(cleanMoney(event.target.value))}
              onFocus={(event) => event.target.select()}
              disabled={isSaving}
              className="pr-8 text-right tabular-nums"
            />
            <span className="text-muted-foreground pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm">
              %
            </span>
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
        <Button
          disabled={!canSave}
          isLoading={isSaving}
          onClick={() => void run(() => onSave(parsed))}
        >
          {isSaving ? 'Saving...' : 'Save sales tax'}
        </Button>
        {onRemove && (
          <Button
            variant="ghost"
            className="text-destructive"
            disabled={isSaving}
            onClick={() => void run(onRemove)}
          >
            Remove sales tax
          </Button>
        )}
      </div>
    </DialogOrSheet>
  );
};
